import { Reveal } from "@/components/Reveal";
import {
  ShieldCheck,
  AlertOctagon,
  Network,
  Fingerprint,
  Cpu,
  Workflow,
  Lock,
  RefreshCcw,
  Layers,
} from "lucide-react";
import { Link } from "react-router-dom";

const PILLARS = [
  { i: AlertOctagon, t: "The threat landscape", d: "Phishing, impersonation, coercion, and supply-chain risk dominate institutional incidents." },
  { i: Lock, t: "Why fragile secrets fail", d: "Seed phrases and software signers were not built for regulated environments." },
  { i: Layers, t: "Why MPC is not enough", d: "MPC distributes keys but does not solve identity, segregation, or governance." },
  { i: Fingerprint, t: "Identity-bound control", d: "Verified human presence, on-card biometrics, no cloud copies." },
  { i: Cpu, t: "Hardware-segregated key architecture", d: "Per-user secure elements. No pooled custody. No zone keys." },
  { i: Workflow, t: "Policy-enforced governance", d: "Initiator / verifier / approver, limits, audit, KYC and Travel Rule." },
  { i: ShieldCheck, t: "Duress protection", d: "Duress fingerprints, silent alerts, and policy-driven escalation." },
  { i: RefreshCcw, t: "Recovery without seed phrases", d: "Biometric-secured backups, successor controls, policy-controlled inheritance." },
  { i: Network, t: "No single point of failure", d: "Card, vault, and platform must agree — three independent layers." },
];

export default function Security() {
  return (
    <main className="bg-white" data-testid="security-page">
      <section className="mt-20 pt-12 md:pt-20 pb-20 dark-panel relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark opacity-40 pointer-events-none" />
        <div
          className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(59,130,246,0.18) 0%, transparent 60%)",
          }}
        />
        <div className="max-w-7xl mx-auto px-6 md:px-8 relative">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-white/65">
              Security
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-6 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight text-white max-w-4xl">
              Security that does not depend on secrets.
            </h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-7 max-w-2xl text-lg text-white/70">
              Vaultavo is built to neutralize the modern threat landscape — designed for the attacks
              institutions actually face.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-white" data-testid="security-pillars">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {PILLARS.map((p, i) => (
              <Reveal key={p.t} delay={(i % 3) * 90}>
                <div className="h-full rounded-2xl border border-[#06102A]/10 bg-white p-7 hover:border-[#06102A] hover:-translate-y-1 transition-all duration-500">
                  <div className="flex items-center justify-between">
                    <div className="h-11 w-11 rounded-xl bg-[#06102A] text-white grid place-items-center">
                      <p.i className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <span className="font-mono-vt text-xs text-[#06102A]/40">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <h3 className="mt-6 font-display text-xl font-medium text-[#06102A]">{p.t}</h3>
                  <p className="mt-3 text-sm text-[#06102A]/70 leading-relaxed">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32 bg-[#FAFBFC]" data-testid="security-cta">
        <div className="max-w-7xl mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#06102A]/65">
              Designed for the attacks institutions actually face
            </p>
            <h2 className="mt-5 font-display text-4xl md:text-5xl font-medium leading-tight text-[#06102A]">
              Three independent layers. One unified system.
            </h2>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact"
              data-testid="security-cta-demo"
              className="inline-flex items-center gap-2 rounded-full bg-[#06102A] text-white text-sm font-medium px-7 py-4 hover:-translate-y-0.5 transition-all"
            >
              Request a Demo
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
